import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Target, Eye, Heart } from 'lucide-react';

const AboutSection = () => {
  const { t, isRTL } = useLanguage();

  const stats = [
    { value: '10+', label: t('about.stat1') },
    { value: '500+', label: t('about.stat2') },
    { value: '2000+', label: t('about.stat3') },
    { value: '24/7', label: t('about.stat4') },
  ];

  return (
    <section id="about" className="py-24 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-200 to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-blue-50 text-brand-blue text-sm font-semibold rounded-full mb-4">
            {t('about.badge')}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
            {t('about.title1')}
            <span className="text-brand-blue"> {t('about.title2')}</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t('about.intro')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div className={`space-y-8 ${isRTL ? 'lg:order-2' : ''}`}>
            <p className="text-slate-600 leading-relaxed">
              {t('about.description')}
            </p>
            
            {/* Mission, Vision, Values */}
            <div className="space-y-6">
              <div className="flex gap-4 p-5 bg-slate-50 rounded-2xl hover:bg-blue-50/50 transition-colors duration-300">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-brand-blue" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 mb-1">{t('about.mission')}</h3>
                  <p className="text-sm text-slate-600">{t('about.missionDesc')}</p>
                </div>
              </div>
              
              <div className="flex gap-4 p-5 bg-slate-50 rounded-2xl hover:bg-red-50/50 transition-colors duration-300">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <Eye className="w-6 h-6 text-brand-red" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 mb-1">{t('about.vision')}</h3>
                  <p className="text-sm text-slate-600">{t('about.visionDesc')}</p>
                </div>
              </div>
              
              <div className="flex gap-4 p-5 bg-slate-50 rounded-2xl hover:bg-blue-50/50 transition-colors duration-300">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Heart className="w-6 h-6 text-brand-blue" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 mb-1">{t('about.values')}</h3>
                  <p className="text-sm text-slate-600">{t('about.valuesDesc')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className={`grid grid-cols-2 gap-6 ${isRTL ? 'lg:order-1' : ''}`}>
            {stats.map((stat, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-slate-50 to-white p-8 rounded-2xl border border-slate-100 hover:border-blue-200 hover:shadow-lg hover:shadow-blue-100/50 transition-all duration-300 group"
              >
                <p className="text-4xl sm:text-5xl font-bold text-brand-blue mb-2 group-hover:scale-105 transition-transform duration-300" dir="ltr">
                  {stat.value}
                </p>
                <p className="text-slate-600 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
